import { MessagingClient as MercuryFxMessagingClient } from './MercuryFxMessagingClient';
import { Log } from '../AuxillaryService/Log';
import { IMessagingClient } from './IMessagingClient';
import { setLogRelay, setMessagingClient, setPushReceiver, setRpcHost } from './instance';
import { Signage, getPlayerUuid, getVersion, signageReload, signageRebootSystem } from '../Signage';
import {
    NotImplementedError,
    platform,
    supportsPlayerPackage,
    supportsScreenshot,
    supportsVideoOutput,
} from '../Platform';
import { IRpcHost } from './IRpcHost';
import { IPushReceiver } from './IPushReceiver';
import { ILogRelay } from '../Logging/ILogRelay';
import { LocalCache } from '../Cache/LocalCache';
import { PromisedIndexedDB } from '../Utility/PromisedIndexedDB';
import { ImageCache } from '../Cache/ImageCache';
import * as Debugging from '../Debugging';
import { isNullish } from '../Utility/Nullish';
import { RpcName, RpcHandler } from '@reachmedianetwork/mercury-fx-client';

/**
 * Initialize the messaging client.
 */
export async function initializeMessagingClient(): Promise<void> {
    Log.Debug('connecting MercuryFxMessagingClient to messagingClient', null, __fileline);

    const mercuryFxClient: IMessagingClient & IRpcHost & IPushReceiver & ILogRelay =
        new MercuryFxMessagingClient(getPlayerUuid(), await platform().getHardwareId(), {
            signageVersion: getVersion(),
            platform: platform().name,
        });

    setMessagingClient(mercuryFxClient);
    setLogRelay(mercuryFxClient);
    setPushReceiver(mercuryFxClient);
    setRpcHost(mercuryFxClient);

    initializeRpc(mercuryFxClient);

    mercuryFxClient.connect().catch(function (e: Error) {
        Log.Warning('Failed to connect messaging client: ' + e.toString(), null, __fileline, e);
    });
}

function initializeRpc(rpcHost: IRpcHost) {
    const thePlatform = platform();

    rpcHost.declareRpc(RpcName.Ping, async (): Promise<'pong'> => 'pong');

    rpcHost.declareRpc(RpcName.GetUptime, async () => Signage.GetUptime());

    rpcHost.declareRpc(RpcName.Reboot, async () => {
        setTimeout(() => void signageRebootSystem(), 0);
    });

    rpcHost.declareRpc(RpcName.Reload, async () => {
        setTimeout(() => void signageReload(), 0);
    });

    rpcHost.declareRpc(RpcName.GetNamedCacheValue, async (keyName: string) => {
        return (await thePlatform.getNamedValue(keyName)) ?? null;
    });

    rpcHost.declareRpc(RpcName.SetNamedCacheValue, (key, value) =>
        thePlatform.setNamedValue(key, value)
    );

    rpcHost.declareRpc(RpcName.DeleteNamedCacheValue, (key) => thePlatform.deleteNamedValue(key));

    rpcHost.declareRpc(RpcName.GetScreenshot, async () => {
        if (!supportsScreenshot(thePlatform)) {
            throw new NotImplementedError('getScreenshot is not supported on this platform');
        }

        //caller is expecting only the base64 image
        return (await thePlatform.getScreenshot()).base64Image;
    });

    rpcHost.declareRpc(RpcName.ListLocalCache, async () => {
        const database = new PromisedIndexedDB(LocalCache.DB_NAME, LocalCache.DB_OBJECT_STORE);

        try {
            return await database.getKeys('cache_id');
        } catch (msg) {
            const errMsg = `Failed to get list of LocalCache IDs: ${String(msg)}`;
            Log.Error(errMsg, null, __fileline, msg);

            throw new Error(errMsg);
        }
    });

    rpcHost.declareRpc(RpcName.GetLocalCacheItem, async (cacheKey: string) => {
        const database = new PromisedIndexedDB(LocalCache.DB_NAME, LocalCache.DB_OBJECT_STORE);
        let data: Awaited<ReturnType<RpcHandler<RpcName.GetLocalCacheItem>>>;

        try {
            data = await database.getRecord('cache_id', cacheKey);

            if (isNullish(data)) {
                return {
                    cache_id: cacheKey,
                    url: '',
                    accessed: -1,
                    id: -1,
                };
            }
        } catch (msg) {
            const errMsg = `Failed to get list of LocalCache IDs: ${String(msg)}`;
            Log.Error(errMsg, null, __fileline, msg);
            throw new Error(errMsg);
        }

        if (data.content instanceof Blob) {
            try {
                const dataURL = await ImageCache.blobToDataURL(data.content);

                data.content = dataURL;

                return data;
            } catch (msg) {
                Log.Error(
                    'Failed to convert Blob to Data URL `' + cacheKey + '`',
                    null,
                    __fileline,
                    { error: msg }
                );

                throw new Error(`Failed to convert Blob to Data URL \`${cacheKey}\`: ${msg}`);
            }
        } else {
            return data;
        }
    });

    rpcHost.declareRpc(RpcName.DeleteLocalCacheItem, async (cacheKey: string) => {
        const database = new PromisedIndexedDB(LocalCache.DB_NAME, LocalCache.DB_OBJECT_STORE);
        try {
            await database.deleteRecord('cache_id', cacheKey);

            return true;
        } catch (msg) {
            Log.Error('Failed to delete LocalCache ID `' + cacheKey + '`', null, __fileline, {
                message: msg,
            });

            throw new Error(`Failed to delete LocalCache ID \`${cacheKey}\`: ${msg}`);
        }
    });

    rpcHost.declareRpc(RpcName.GetListOfRootStorageFiles, async () => {
        if (isNullish(thePlatform.getListOfRootStorageFiles)) {
            throw new NotImplementedError(
                'This platform does not support retrieving file information from root storage.'
            );
        }

        return (await thePlatform.getListOfRootStorageFiles()).diFiles;
    });

    rpcHost.declareRpc(RpcName.GetRootStorageFile, async (fileName: string) => {
        if (isNullish(thePlatform.getRootStorageFile)) {
            throw new NotImplementedError(
                'This platform does not support retrieving files from root storage.'
            );
        } else if (isNullish(fileName) || fileName === '') {
            throw new Error('A file name must be provided for this operation.');
        }

        return (await thePlatform.getRootStorageFile(fileName)).fiString;
    });

    rpcHost.declareRpc(RpcName.ClearCache, async () => {
        if (isNullish(thePlatform.clearBrowserCacheOnReboot)) {
            throw new NotImplementedError(
                'This platform does not support clearing browser cache on reboot.'
            );
        }

        await thePlatform.clearBrowserCacheOnReboot();

        setTimeout(() => void signageRebootSystem(), 0);
    });

    rpcHost.declareRpc(RpcName.GetSystemConfig, async () => {
        const sysInfo = await Signage.SystemInformation();

        //SystemData type says timeline will be number. I have observed undefined and string in
        //production but never number. So we'll support string, number, and undefined.
        let timelineId: string | undefined = undefined;
        switch (typeof sysInfo.player.timelineId) {
            case 'number':
                timelineId = sysInfo.player.timelineId.toString();
                break;
            case 'string':
                timelineId = sysInfo.player.timelineId;
                break;
        }

        return {
            ...sysInfo,
            player: {
                ...sysInfo.player,
                timelineId,
            },
        };
    });

    rpcHost.declareRpc(RpcName.GetApplicationState, async () => {
        if (isNullish(thePlatform.getApplicationState)) {
            throw new NotImplementedError(
                'This platform does not support getting application state.'
            );
        }

        return await thePlatform.getApplicationState();
    });

    rpcHost.declareRpc(RpcName.DisableVideoOutput, async () => {
        if (!supportsVideoOutput(thePlatform)) {
            throw new NotImplementedError('This platform does not support disabling video output.');
        }

        return await thePlatform.disableVideoOutput(true);
    });

    rpcHost.declareRpc(RpcName.EnableVideoOutput, async () => {
        if (!supportsVideoOutput(thePlatform)) {
            throw new NotImplementedError('This platform does not support disabling video output.');
        }

        return !(await thePlatform.disableVideoOutput(false));
    });

    rpcHost.declareRpc(RpcName.ShowNetworkConnectivity, async () => {
        Debugging.NetworkConnectivity.show();
    });

    rpcHost.declareRpc(RpcName.HideNetworkConnectivity, async () => {
        Debugging.NetworkConnectivity.hide();
    });

    rpcHost.declareRpc(RpcName.ShowVideoInfo, async () => {
        Debugging.VideoInfo.show();
    });

    rpcHost.declareRpc(RpcName.HideVideoInfo, async () => {
        Debugging.VideoInfo.hide();
    });

    rpcHost.declareRpc(
        RpcName.SetLogLevel,
        async (newLevel: 'info' | 'warn' | 'error' | 'debug') => {
            if (isNullish(thePlatform.setLogLevel)) {
                throw new NotImplementedError('This platform does not support setting log level.');
            }

            await thePlatform.setLogLevel(newLevel);
        }
    );

    rpcHost.declareRpc(RpcName.EnablePersistentLog, async () => {
        if (isNullish(thePlatform.enablePersistentLog)) {
            throw new NotImplementedError('This platform does not support persistent logs.');
        }

        await thePlatform.enablePersistentLog();
    });

    rpcHost.declareRpc(RpcName.DisablePersistentLog, async () => {
        if (isNullish(thePlatform.disablePersistentLog)) {
            throw new NotImplementedError('This platform does not support persistent logs.');
        }

        await thePlatform.disablePersistentLog();
    });

    rpcHost.declareRpc(RpcName.RestartApplication, async () => {
        if (isNullish(thePlatform.restartApplication)) {
            throw new NotImplementedError('Feature is not implemented on this platform.');
        }

        await thePlatform.restartApplication();
    });

    rpcHost.declareRpc(
        RpcName.RedeployPackage,
        async (
            postbackChannelId: string,
            packageGuid: string,
            packageVersion: string,
            purgePackage: boolean
        ) => {
            if (!supportsPlayerPackage(thePlatform)) {
                throw new NotImplementedError(
                    'This platform does not support player package deployments.'
                );
            }

            await thePlatform.deployPackage(
                postbackChannelId,
                getPlayerUuid() || '0',
                packageGuid,
                packageVersion,
                purgePackage
            );
        }
    );

    rpcHost.declareRpc(RpcName.GetInstalledPackages, async () => {
        if (!supportsPlayerPackage(thePlatform)) {
            throw new NotImplementedError(
                'This platform does not support player package deployments.'
            );
        }

        return await thePlatform.getInstalledPackages();
    });

    rpcHost.declareRpc(RpcName.InvokeCustomRpc, (customRpcName: string, customArgs: unknown[]) => {
        return rpcHost.invokeCustomRpc(customRpcName, customArgs);
    });
}
